import { MapFavourite } from '@shared/models/map-interfaces';
import { BcmInvoiceWdTemplate } from '@shared/models/bcm-invoice-wd-template';
import { DEFAULT_BERTH_RESERVATION_UNIT } from '@modules/bcm/settings/default-units/default-units.component';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';

export enum FooterLayout {
    Row = 'row',
    Columns = 'columns',
    Editor = 'editor',
}

export enum TextAlign {
    Left = 'left',
    Center = 'center',
    Right = 'right'
}


export enum BcmSettingsSectionName {
    AssociationReports = 'association_reports',
    BerthsGraphically = 'berths-graphically',
    Dashboard = 'dashboard',
    Datev = 'datev',
    DefaultUnits = 'defaultUnits',
    DefaultBookingType = 'defaultBookingType',
    DefaultBerthReservationTimeUnit = 'defaultBerthReservationTimeUnit',
    DockSite = 'scaleRegistration',
    ElectricMeter = 'electricMeter',
    ElectricMeterAlarm = 'electricMeterAlarm',
    Import = 'import',
    InvoiceTemplate = 'invoice_template',
    InvoiceDefaultTemplates = 'invoice_default_templates',
    MailServer = 'mail_server',
    MapEditor = 'mapEditor',
    PersonsOrganisations = 'personsOrganisations',
    Tenant = 'tenant',
    UISettings = 'uiSettings',
    PinCode = 'pin_code',
    BetaFeatures = 'betaFeatures',
    OrderManagementRollout = 'orderManagementRollout',
    Employees = 'employees',
    OnlineBooking = 'onlineBooking',
}

export interface BcmBetaFeaturesSettings extends BcmSettingsSection {
    bookingDialogV2?: boolean;
    pricingV2?: boolean;
}

export interface BcmOrderManagementRolloutSettings extends BcmSettingsSection {
    orderFormV2?: boolean;
    auditHistoryTab?: boolean;
    offlineSyncV2?: boolean;
    billingTransferV2?: boolean;
    timeTrackingV2?: boolean;
    contractValidationV2?: boolean;
    dockSiteInfoSheetV2?: boolean;
    orderListV2?: boolean;
    orderDetailV2?: boolean;
}

export interface BcmEmployeesSettings extends BcmSettingsSection {
    employeeNumberTemplatePrefix?: string;
    employeeNumberCurrent?: number;
    workTimeGeolocationEnabled?: boolean;
    workTimeGeolocationDisclaimerAcceptedAt?: string | null;
    workTimeGeolocationDisclaimerAcceptedBy?: string | null;
    workTimeGeolocationDisclaimerVersion?: string | null;
    webWorkingTimeSelfServiceMode?: 'live_stamp_only' | 'full_self_service';
}

export interface BcmSettingsSection {
    [key: string]: any;
}

export interface BcmSettings {
    // Sort it a-z please
    [BcmSettingsSectionName.AssociationReports]: BcmAssociationReportsSettings;
    [BcmSettingsSectionName.BerthsGraphically]: BcmBerthGraphicallySettings;
    [BcmSettingsSectionName.Dashboard]: BcmSettingsSection;
    [BcmSettingsSectionName.Datev]: BcmDatevSettings;
    [BcmSettingsSectionName.DefaultUnits]: BcmDefaultUnitsSettings;
    [BcmSettingsSectionName.DefaultBookingType]: BcmDefaultBookingTypeSettings;
    [BcmSettingsSectionName.DefaultBerthReservationTimeUnit]: DefaultBerthReservationTimeUnit;
    [BcmSettingsSectionName.DockSite]: BcmDockSiteSettings;
    [BcmSettingsSectionName.ElectricMeter]: BcmElectricMeterSettings;
    [BcmSettingsSectionName.ElectricMeterAlarm]: BcmElectricMeterAlarmSettings;
    [BcmSettingsSectionName.Import]: BcmImportSettings;
    [BcmSettingsSectionName.InvoiceTemplate]: BcmInvoiceTemplateSettings;
    [BcmSettingsSectionName.InvoiceDefaultTemplates]: BcmInvoiceDefaultTemplatesSettings;
    [BcmSettingsSectionName.MailServer]: BcmMailServerSettings;
    [BcmSettingsSectionName.MapEditor]: BcmMapEditorSettings;
    [BcmSettingsSectionName.PersonsOrganisations]: BcmPersonsOrganisationsSettings;
    [BcmSettingsSectionName.Tenant]: BcmTenantSettings;
    [BcmSettingsSectionName.BetaFeatures]: BcmBetaFeaturesSettings;
    [BcmSettingsSectionName.Employees]: BcmEmployeesSettings;
    [BcmSettingsSectionName.OnlineBooking]: BcmOnlineBookingSettings;
    [BcmSettingsSectionName.OrderManagementRollout]: BcmOrderManagementRolloutSettings;
}

export interface BcmDatevSettings extends BcmSettingsSection {
    consultantNumber: string; // Beraternummer
    clientNumber: string; // Mandantennummer
    payInAccount?: string; // Gegenkonto Kassen-Einzahlung
    payOutAccount?: string; // Gegenkonto Kassen-Auszahlung
}

export type SmtpAuthMode = 'PASSWORD' | 'M365_OAUTH';

export interface BcmMailServerSettings extends BcmSettingsSection {
    smtpUrl: string;
    smtpSSLPort: number;
    imapUrl: string;
    imapSSLPort: number;
    name: string;
    mail: string;
    user?: string;
    password?: string;

    // Auth mode (default: PASSWORD for backwards compatibility)
    authMode?: SmtpAuthMode;

    // M365 OAuth fields
    mailboxUpn?: string;
    tenantId?: string;
    clientId?: string;
    clientSecret?: string;
    clientSecretSet?: boolean; // Backend indicator - true when secret is already configured
}

export interface BcmElectricMeterSettings extends BcmSettingsSection {
    productID: number;
}

/**
 * Alarm-Mail des Stromschrankmonitor-Widgets. Mandantenweit statt im Widget-metaData,
 * weil die Meldung aus dem externen Saeulen-API-Call entsteht und dort kein Dashboard
 * eines Nutzers existiert.
 */
export interface BcmElectricMeterAlarmSettings extends BcmSettingsSection {
    alarmMailEnabled?: boolean;
    alarmMailRecipients?: string[];
    alarmMailTemplateId?: number | null;
    alarmMailCooldownMinutes?: number;
    doorOpenAlarmDelayMinutes?: number;
    /** Meldungstypen, die als Alarm gelten. Fehlt sie, gilt der Standard des Backends. */
    alarmTypes?: string[];
}

export interface BcmInvoiceTemplateSettingsFooter {
    headline?: string;
    text: string;
    align: TextAlign;
}

export interface BcmInvoiceTemplateSettings extends BcmSettingsSection {
    header?: string;
    showAdditionalDataUnderLogo?: boolean;
    additionalDataUnderLogo?: string;
    additionalDataUnderLogoAlign?: TextAlign;
    showBankDataInHeader?: boolean;
    showTenantRelationInHeader?: boolean;
    showAccount?: boolean;
    showItemNumber?: boolean;
    footerLayout?: FooterLayout;
    footer?: string;
    footerAlign?: TextAlign;
    footers?: BcmInvoiceTemplateSettingsFooter[];
    defaultNote?: string;
    defaultPaymentType?: string;
    dueDateDefaultEmpty?: boolean;
    dueDateDefaultDays?: number;
    dueDateNoteInvoice?: string;
    dueDateNoteDirectDebit?: string;
    certificateOfAchievementNote?: string;
    logoId?: number;
    showSinglePriceInNet?: boolean;
    roundPricesTo5SwissCentimes?: boolean;
    showSepaQrCode?: boolean;
    defaultPositionsSortingColumn?: string | null;
    defaultPositionsSortingDirection?: 'asc' | 'desc';
    eInvoiceEnabled?: boolean;
    eInvoiceAttachSeparateXmlOnMail?: boolean;
    eInvoiceValidateXml?: boolean;
}

export interface BcmInvoiceDefaultTemplatesSettings extends BcmSettingsSection {
    [type: string]: BcmInvoiceWdTemplate;

    // invoiceTemplate: BcmInvoiceWdTemplate,
    // cancellationInvoiceTemplate: BcmInvoiceWdTemplate,
    // creditingTemplate: BcmInvoiceWdTemplate,
    // cancellationCreditingTemplate: BcmInvoiceWdTemplate,
    // receiptTemplate: BcmInvoiceWdTemplate,
    // cancellationReceiptTemplate: BcmInvoiceWdTemplate
}

export interface BcmAssociationReportsSettings extends BcmSettingsSection {

    form: string;
    nonProfit: boolean;
    nonProfitDate: Date;

    chairman1_name?: string;
    chairman1_mail?: string;
    chairman1_phone?: string;
    chairman1_postcode?: string;
    chairman1_city?: string;

    chairman2_name?: string;
    chairman2_mail?: string;
    chairman2_phone?: string;
    chairman2_postcode?: string;
    chairman2_city?: string;

    treasurer_name?: string;
    treasurer_mail?: string;
    treasurer_phone?: string;
    treasurer_postcode?: string;
    treasurer_city?: string;

    secretary_name?: string;
    secretary_mail?: string;
    secretary_phone?: string;
    secretary_postcode?: string;
    secretary_city?: string;

    youth_leader_name?: string;
    youth_leader_mail?: string;
    youth_leader_phone?: string;
    youth_leader_postcode?: string;
    youth_leader_city?: string;

    tenantRelationsHonor: number[];
    tenantRelationsActive: number[];
    tenantRelationsInactive: number[];
    tenantRelationsPaying: number[];
    tenantRelationsGuest: number[];
}

export interface BcmPersonsOrganisationsSettings extends BcmSettingsSection {
    automaticIdentNumber: boolean;
    sharedIdentNumber: boolean;

    sharedIdentNumberCurrent: number;
    sharedIdentNumberTemplatePrefix: string;
    sharedIdentNumberTemplateSuffix: string;

    personsIdentNumberCurrent: number;
    personsIdentNumberTemplatePrefix: string;
    personsIdentNumberTemplateSuffix: string;

    companiesIdentNumberCurrent: number;
    companiesIdentNumberTemplatePrefix: string;
    companiesIdentNumberTemplateSuffix: string;
}

export interface BcmDockSiteSettings extends BcmSettingsSection {
    ipV4: string;
    port: number;
    isBlockedBy: string;
    productIdsWeighting: number[];
    goodwillWeightThreshold: number;
    productIdsSlipping: number[];
    orderConfirmationFooterText?: string;
    orderConfirmationTemplateMode?: 'standard' | 'word';
    orderConfirmationWordTemplateId?: number | null;
    allowDirectSettlement?: boolean;
    paymentMethodCash?: boolean;
    paymentMethodEcCard?: boolean;
    paymentMethodCreditCard?: boolean;
    docksitePriceVisibilityMode?: DocksitePriceVisibilityMode;
    priceVisibleCategoryIds?: number[];
    traverses?: DocksiteTraverse[];
    scaleMaxWeightKg?: number;
}

export interface DocksiteTraverse {
    id: string;
    name: string;
    weightKg: number;
}

export type DocksitePriceVisibilityMode = 'all' | 'none' | 'custom';

export type DocksiteShowPriceOverride = 'default' | 'show' | 'hide';

export interface BcmDefaultUnitsSettings extends BcmSettingsSection {
    // Tagesgastlieger
    dayGuestBoater: number;
}

export interface BcmDefaultBookingTypeSettings extends BcmSettingsSection {
    type: BookingType;
}

export interface DefaultBerthReservationTimeUnit extends BcmSettingsSection {
    type: DEFAULT_BERTH_RESERVATION_UNIT | '';
    fromMinutes: number | '';
    fromHours: number | '';
    toMinutes: number | '';
    toHours: number | '';
    dailyGuestFromHours: number;
    dailyGuestFromMinutes: number;
    dailyGuestToHours: number;
    dailyGuestToMinutes: number;
    overnightGuestFromHours: number;
    overnightGuestFromMinutes: number;
    overnightGuestToHours: number;
    overnightGuestToMinutes: number;
    useDayBookings?: boolean;
}

export interface BcmMapEditorSettings extends BcmSettingsSection {
    isBlockedBy: string;
    isBlockedById: string;
    isBlockedSince: string | Date;
    favourites: MapFavourite[];
}

export interface BcmTenantSettings extends BcmSettingsSection {
    readonly startOfSubscriptionScheduler: string | Date;
}

export interface BcmBerthGraphicallySettings extends BcmSettingsSection {
    Items: any[]; // add type..
}

export interface BcmImportSettings extends BcmSettingsSection {
    berth: Date;
    winterStorage: Date;
    product: Date;
    productRelation: Date;
    person: Date;
    company: Date;
    electricMeter: Date;
    electricMeterCabinet: Date;
    boat: Date;
}

export type OnlineBookingBerthSelectionMode = 'user_choice' | 'auto_smallest_fit';

export type OnlineBookingFieldMode = 'required' | 'optional';
export type OnlineBookingFieldStep = 'search' | 'contact';

export interface OnlineBookingFormFieldRule {
    field: string;
    mode: OnlineBookingFieldMode;
    step?: OnlineBookingFieldStep;
}

export interface BcmOnlineBookingSettings extends BcmSettingsSection {
    enabled: boolean;
    berthSelectionMode: OnlineBookingBerthSelectionMode;
    /** @deprecated Use websiteAllowFutureBookings / qrAllowFutureBookings */
    allowFutureBookings: boolean;
    /** @deprecated Use websiteMaxFutureDays / qrMaxFutureDays */
    maxFutureDays: number;
    websiteAllowFutureBookings: boolean;
    websiteMaxFutureDays: number;
    qrAllowFutureBookings: boolean;
    qrMaxFutureDays: number;
    appBookingEnabled: boolean;
    appAllowFutureBookings: boolean;
    appMaxFutureDays: number;
    pendingBookingTtlMinutes: number;
    /** @deprecated Legacy list – use formFields */
    requestedFields: string[];
    formFields?: OnlineBookingFormFieldRule[];
    formFieldSteps?: Partial<Record<string, OnlineBookingFieldStep>>;
    /** Maps active interface fields to wizard masks (search | contact | documents). Unassigned fields are omitted. */
    formFieldLayout?: Record<string, 'search' | 'contact' | 'documents'>;
    headerText: string;
    disclaimerText: string;
    primaryColor: string;
    accentColor: string;
    backgroundColor: string;
    logoFileId: number | null;
    agbFileId: number | null;
    confirmationMailingId: number | null;
    invoiceMailingId: number | null;
    /** Word invoice template for the paid online-booking invoice. Required while e-invoicing is on. */
    invoiceWdTemplateId?: number | null;
    apiClientId: number | null;
    /** @deprecated use qrStickerIncludeLogo / qrStickerIncludeCaption */
    qrStickerIncludeBranding?: boolean;
    qrStickerIncludeLogo: boolean;
    qrStickerIncludeCaption: boolean;
    qrBulkLastRunAt?: string | null;
    qrBulkStickerSignature?: string | null;
    approachInstructions?: string;
    harborPlanFileId?: number | null;
}
